/**
 * SettingsView.jsx — Dialog de CONFIGURAÇÕES da conta (casca/shell).
 * Pura: recebe { vm, actions }. Contrato: shell/settings.vm.js (@version 4).
 *   vm      → open, userName, email, emailNotificationsEnabled, savingEmailPref,
 *             editName { open, value, error, saving },
 *             businessType { currentId, options[], saving },
 *             documents { loading, error, canEdit, accountDocument, billingName, billingDocument },
 *             editDocuments { open, kind, title, helper, showName, name, nameError, document,
 *                             documentError, formError, saving }
 *   actions → onClose, onToggleEmailNotifications, onOpenEditName, onChangeEditNameValue,
 *             onSaveEditName, onCancelEditName, onOpenTerms, onDeleteAccount, onLogout,
 *             onChangeBusinessType, onOpenEditAccountDocument, onOpenEditBilling,
 *             onChangeEditDocument, onChangeEditDocumentsName, onSaveEditDocuments,
 *             onCancelEditDocuments
 * Estilo só via `sx` + CSS vars (var(--*)). Fullscreen no mobile.
 * A View não valida nada — erro do campo nome vem de vm.editName.error.
 */
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton,
  Button, TextField, Switch, Stack, Divider, CircularProgress, useMediaQuery, Tooltip,
  Select, MenuItem, Alert,
} from '@mui/material';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import Close from '@mui/icons-material/Close';
import EditOutlined from '@mui/icons-material/EditOutlined';
import MailOutline from '@mui/icons-material/MailOutline';
import PersonOutline from '@mui/icons-material/PersonOutline';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Logout from '@mui/icons-material/Logout';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

// Menu (Select) pintado com as vars — evita o Paper branco do MUI em temas escuros.
const selectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: 'var(--surface)',
      color: 'var(--text-primary)',
      border: '1px solid var(--divider)',
      borderRadius: 'var(--radius-sm)',
      boxShadow: 'var(--shadow-hover)',
      maxHeight: 320,
      '& .MuiMenuItem-root': { fontSize: 14, minHeight: 40 },
      '& .MuiMenuItem-root:hover': { bgcolor: 'var(--color-primary-soft)' },
      '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary)', fontWeight: 700 },
      '& .MuiMenuItem-root.Mui-selected:hover': { bgcolor: 'var(--color-primary-soft)' },
    },
  },
};

const paperSx = {
  bgcolor: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: { xs: 0, sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-hover)',
  backgroundImage: 'none',
};

/* Linha de item de configuração: ícone + rótulo/valor + controle à direita */
function Row({ icon, label, value, control }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: 56, px: { xs: 2, sm: 3 } }}>
      <Box aria-hidden sx={{
        width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--radius-sm)',
        bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary)',
        display: 'grid', placeItems: 'center',
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 'var(--fw-medium)', color: 'var(--text-primary)' }}>
          {label}
        </Typography>
        {value != null && (
          <Typography noWrap variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
            {value}
          </Typography>
        )}
      </Box>
      {control}
    </Stack>
  );
}

/* Botão "Editar" das linhas (mesmo desenho do de Nome). */
function EditButton({ onClick, disabled, label = 'Editar' }) {
  return (
    <Button
      onClick={onClick}
      disabled={!!disabled}
      startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
      sx={{
        minHeight: 40, px: 1.5, textTransform: 'none', fontWeight: 'var(--fw-medium)', flexShrink: 0,
        color: 'var(--color-primary)', borderRadius: 'var(--radius-sm)',
        '&:hover': { bgcolor: 'var(--color-primary-soft)' },
        '&.Mui-disabled': { color: 'var(--text-secondary)' },
        '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
      }}
    >
      {label}
    </Button>
  );
}

const SUB_FIELD_SX = {
  '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-primary)' },
  '& .MuiInputLabel-root.Mui-error': { color: 'var(--color-error)' },
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 'var(--fs-body2)',
    '& fieldset': { borderColor: 'var(--divider)' },
    '&:hover fieldset': { borderColor: 'var(--color-primary-light)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
    '&.Mui-error fieldset': { borderColor: 'var(--color-error)' },
  },
  '& .MuiFormHelperText-root': { color: 'var(--text-secondary)' },
  '& .MuiFormHelperText-root.Mui-error': { color: 'var(--color-error)' },
};

function SectionLabel({ children }) {
  return (
    <Typography variant="overline" sx={{
      display: 'block', px: { xs: 2, sm: 3 }, pt: 2, pb: .5,
      color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em',
    }}>
      {children}
    </Typography>
  );
}

export default function SettingsView({ vm, actions }) {
  const fullScreen = useMediaQuery('(max-width:599px)'); // UI-local (responsivo)
  const { editName = {}, businessType = {}, documents = {}, editDocuments = {} } = vm || {};
  const btOptions = businessType.options || [];
  const btCurrent = btOptions.find((o) => o.id === businessType.currentId);

  return (
    <>
      {/* ---------------- Dialog principal: Configurações da conta ---------------- */}
      <Dialog
        open={!!vm?.open}
        onClose={() => actions?.onClose?.()}
        fullScreen={fullScreen}
        maxWidth="xs"
        fullWidth
        aria-labelledby="settings-title"
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Stack direction="row" alignItems="center" sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid var(--divider)' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="settings-title" component="span" sx={{
                display: 'block', fontWeight: 'var(--fw-bold)',
                fontSize: 'var(--fs-h6)', lineHeight: 1.2,
              }}>
                Configurações da conta
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                Dados pessoais, notificações e privacidade
              </Typography>
            </Box>
            <IconButton aria-label="Fechar configurações" onClick={() => actions?.onClose?.()}
              sx={{
                width: 40, height: 40, color: 'var(--text-secondary)',
                '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--color-primary-soft)' },
                '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
              }}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { pt: 0 } }}>
          <SectionLabel>Conta</SectionLabel>

          <Row
            icon={<PersonOutline fontSize="small" />}
            label="Nome"
            value={vm?.userName}
            control={
              <Button
                onClick={() => actions?.onOpenEditName?.()}
                startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 40, px: 1.5, textTransform: 'none', fontWeight: 'var(--fw-medium)',
                  color: 'var(--color-primary)', borderRadius: 'var(--radius-sm)',
                  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
                }}
              >
                Editar
              </Button>
            }
          />

          <Row
            icon={<MailOutline fontSize="small" />}
            label="E-mail"
            value={vm?.email}
            control={
              <Typography variant="caption" sx={{
                px: 1, py: .25, borderRadius: 999, flexShrink: 0,
                border: '1px solid var(--divider)', color: 'var(--text-secondary)', bgcolor: 'var(--bg-page)',
              }}>
                somente leitura
              </Typography>
            }
          />

          <Row
            icon={<BadgeOutlined fontSize="small" />}
            label="CPF ou CNPJ"
            value={documents.accountDocument}
            control={<EditButton onClick={() => actions?.onOpenEditAccountDocument?.()} disabled={!documents.canEdit} />}
          />

          <Divider sx={{ borderColor: 'var(--divider)', mt: 1 }} />
          <SectionLabel>Cobrança</SectionLabel>
          <Typography variant="caption" sx={{ display: 'block', px: { xs: 2, sm: 3 }, pb: .5, color: 'var(--text-secondary)' }}>
            Usados nos pagamentos (PIX). Preenchidos na sua primeira compra.
          </Typography>

          <Row
            icon={<BusinessOutlined fontSize="small" />}
            label="Nome ou razão social"
            value={documents.billingName}
            control={<EditButton onClick={() => actions?.onOpenEditBilling?.()} disabled={!documents.canEdit} />}
          />
          <Row
            icon={<ReceiptLongOutlined fontSize="small" />}
            label="CPF ou CNPJ de cobrança"
            value={documents.billingDocument}
          />
          {documents.error && (
            <Typography variant="caption" sx={{ display: 'block', px: { xs: 2, sm: 3 }, pt: .5, color: 'var(--color-error)' }}>
              {documents.error}
            </Typography>
          )}

          <Divider sx={{ borderColor: 'var(--divider)', mt: 1 }} />
          <SectionLabel>Negócio</SectionLabel>

          {/* Tipo de negócio — escolha estável (define labels/ícones do app inteiro).
              Troca via Select; desabilitado enquanto vm.businessType.saving. */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ px: { xs: 2, sm: 3 }, py: .5 }}>
            <Box aria-hidden sx={{
              width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--radius-sm)',
              bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary)',
              display: 'grid', placeItems: 'center',
            }}>
              <StorefrontOutlined fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 'var(--fw-medium)', color: 'var(--text-primary)' }}>
                Tipo de negócio
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mb: 1 }}>
                Define os nomes e ícones do app para o seu segmento. Costuma ser escolhido uma única vez.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Select
                  size="small"
                  fullWidth
                  value={btCurrent ? businessType.currentId : ''}
                  onChange={(e) => actions?.onChangeBusinessType?.(e.target.value)}
                  disabled={!!businessType.saving}
                  IconComponent={KeyboardArrowDown}
                  MenuProps={selectMenuProps}
                  inputProps={{ 'aria-label': 'Tipo de negócio' }}
                  sx={{
                    maxWidth: 320, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
                    borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)',
                    '& .MuiSelect-select': { minHeight: 'unset', py: 1 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--divider)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-primary-light)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-primary)' },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--divider)' },
                    '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
                  }}
                >
                  {btOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </Select>
                {businessType.saving && <CircularProgress size={18} sx={{ color: 'var(--color-primary)', flexShrink: 0 }} />}
              </Stack>
              {businessType.saving && (
                <Typography variant="caption" sx={{ display: 'block', mt: .5, color: 'var(--text-secondary)' }}>
                  Aplicando ao seu negócio…
                </Typography>
              )}
            </Box>
          </Stack>

          <Divider sx={{ borderColor: 'var(--divider)', mt: 1 }} />
          <SectionLabel>Notificações</SectionLabel>

          <Row
            icon={<NotificationsNoneOutlined fontSize="small" />}
            label="Notificações por e-mail"
            value={vm?.savingEmailPref ? 'Salvando…' : 'Avisos de agendamentos e lembretes'}
            control={
              <Stack direction="row" spacing={1} alignItems="center">
                {vm?.savingEmailPref && <CircularProgress size={16} sx={{ color: 'var(--color-primary)' }} />}
                <Switch
                  checked={!!vm?.emailNotificationsEnabled}
                  disabled={!!vm?.savingEmailPref}
                  onChange={(e) => actions?.onToggleEmailNotifications?.(e.target.checked)}
                  inputProps={{ 'aria-label': 'Notificações por e-mail' }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--color-primary)' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'var(--color-primary)' },
                    '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
                  }}
                />
              </Stack>
            }
          />

          <Divider sx={{ borderColor: 'var(--divider)', mt: 1 }} />
          <SectionLabel>Privacidade</SectionLabel>

          <Row
            icon={<GavelOutlined fontSize="small" />}
            label="Termos de Uso & LGPD"
            value="Como tratamos seus dados"
            control={
              <Tooltip title="Abre em nova aba">
                <IconButton aria-label="Abrir Termos de Uso e LGPD em nova aba"
                  onClick={() => actions?.onOpenTerms?.()}
                  sx={{
                    width: 40, height: 40, color: 'var(--text-secondary)',
                    '&:hover': { color: 'var(--color-primary)', bgcolor: 'var(--color-primary-soft)' },
                    '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
                  }}>
                  <OpenInNew sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            }
          />

          {/* Exclusão de conta — gatilho destrutivo discreto (fluxo real é do app) */}
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 3 }}>
            <Button
              onClick={() => actions?.onDeleteAccount?.()}
              startIcon={<DeleteOutline sx={{ fontSize: 18 }} />}
              sx={{
                minHeight: 40, px: 1.5, textTransform: 'none', fontWeight: 'var(--fw-medium)',
                color: 'var(--color-error)', borderRadius: 'var(--radius-sm)',
                '&:hover': { bgcolor: 'var(--tint-error-bg)' },
                '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: 2 },
              }}
            >
              Excluir minha conta
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: .5, color: 'var(--text-secondary)' }}>
              A exclusão é definitiva e pedirá confirmação.
            </Typography>
          </Box>
        </DialogContent>

        {/* Sair — rodapé fixo, largura total. Quem não acha o "Sair" do header cai aqui; sem ele a
            única saída visível seria "Excluir minha conta". Fica fora do scroll: sempre à vista. */}
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid var(--divider)' }}>
          <Button
            fullWidth
            onClick={() => actions?.onLogout?.()}
            startIcon={<Logout sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 44, textTransform: 'none', fontWeight: 'var(--fw-bold)',
              color: 'var(--text-primary)', border: '1px solid var(--divider)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--bg-page)', borderColor: 'var(--color-primary-light)' },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
            }}
          >
            Sair
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Sub-dialog: Editar nome ---------------- */}
      <Dialog
        open={!!editName.open}
        onClose={() => { if (!editName.saving) actions?.onCancelEditName?.(); }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="edit-name-title"
        PaperProps={{ sx: { ...paperSx, borderRadius: 'var(--radius-card)' } }}
      >
        <DialogTitle id="edit-name-title" sx={{
          px: 3, pt: 3, pb: 1,           fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-h6)',
        }}>
          Editar nome
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 0 }}>
          <TextField
            autoFocus
            fullWidth
            value={editName.value ?? ''}
            onChange={(e) => actions?.onChangeEditNameValue?.(e.target.value)}
            disabled={!!editName.saving}
            error={!!editName.error}
            helperText={editName.error || ' '}
            placeholder="Seu nome"
            inputProps={{ 'aria-label': 'Nome da conta' }}
            sx={{
              mt: .5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 'var(--fs-body2)',
                '& fieldset': { borderColor: 'var(--divider)' },
                '&:hover fieldset': { borderColor: 'var(--color-primary-light)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
                '&.Mui-error fieldset': { borderColor: 'var(--color-error)' },
              },
              '& .MuiFormHelperText-root': { color: 'var(--text-secondary)' },
              '& .MuiFormHelperText-root.Mui-error': { color: 'var(--color-error)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => actions?.onCancelEditName?.()}
            disabled={!!editName.saving}
            sx={{
              minHeight: 40, px: 2, textTransform: 'none', fontWeight: 'var(--fw-medium)',
              color: 'var(--text-secondary)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--bg-page)', color: 'var(--text-primary)' },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => actions?.onSaveEditName?.()}
            disabled={!!editName.saving}
            startIcon={editName.saving ? <CircularProgress size={16} sx={{ color: 'var(--on-primary)' }} /> : null}
            sx={{
              minHeight: 40, px: 2.5, textTransform: 'none', fontWeight: 'var(--fw-bold)',
              bgcolor: 'var(--color-primary)', color: 'var(--on-primary)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--color-primary-dark)' },
              '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: .8 },
              '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
            }}
          >
            {editName.saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Sub-dialog: CPF/CNPJ da conta OU dados de cobrança ---------------- */}
      <Dialog
        open={!!editDocuments.open}
        onClose={() => { if (!editDocuments.saving) actions?.onCancelEditDocuments?.(); }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="edit-documents-title"
        PaperProps={{ sx: { ...paperSx, borderRadius: 'var(--radius-card)' } }}
      >
        <DialogTitle id="edit-documents-title" sx={{
          px: 3, pt: 3, pb: .5, fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-h6)',
        }}>
          {editDocuments.title}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mb: 2 }}>
            {editDocuments.helper}
          </Typography>
          <Stack spacing={1}>
            {editDocuments.showName && (
              <TextField
                autoFocus
                fullWidth
                label="Nome ou razão social"
                value={editDocuments.name ?? ''}
                onChange={(e) => actions?.onChangeEditDocumentsName?.(e.target.value)}
                disabled={!!editDocuments.saving}
                error={!!editDocuments.nameError}
                helperText={editDocuments.nameError || ' '}
                autoComplete="organization"
                inputProps={{ maxLength: 255 }}
                sx={SUB_FIELD_SX}
              />
            )}
            <TextField
              autoFocus={!editDocuments.showName}
              fullWidth
              label="CPF ou CNPJ"
              value={editDocuments.document ?? ''}
              onChange={(e) => actions?.onChangeEditDocument?.(e.target.value)}
              disabled={!!editDocuments.saving}
              error={!!editDocuments.documentError}
              helperText={editDocuments.documentError || 'Deixe em branco para não informar.'}
              // CNPJ novo tem LETRA: teclado de texto, nunca inputMode="numeric". Sem maxLength: colar " 12.ABC…" com
              // espaço seria CORTADO pelo navegador antes da máscara limpar — quem limita é mascararDocumento (14).
              inputProps={{ autoCapitalize: 'characters', spellCheck: false, 'aria-label': 'CPF ou CNPJ' }}
              sx={SUB_FIELD_SX}
            />
          </Stack>
          {editDocuments.formError && (
            <Alert severity="error" sx={{
              mb: 1, borderRadius: 'var(--radius-sm)',
              bgcolor: 'var(--tint-error-bg)', color: 'var(--text-primary)',
              '& .MuiAlert-icon': { color: 'var(--color-error)' },
            }}>
              {editDocuments.formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => actions?.onCancelEditDocuments?.()}
            disabled={!!editDocuments.saving}
            sx={{
              minHeight: 40, px: 2, textTransform: 'none', fontWeight: 'var(--fw-medium)',
              color: 'var(--text-secondary)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--bg-page)', color: 'var(--text-primary)' },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => actions?.onSaveEditDocuments?.()}
            disabled={!!editDocuments.saving}
            startIcon={editDocuments.saving ? <CircularProgress size={16} sx={{ color: 'var(--on-primary)' }} /> : null}
            sx={{
              minHeight: 40, px: 2.5, textTransform: 'none', fontWeight: 'var(--fw-bold)',
              bgcolor: 'var(--color-primary)', color: 'var(--on-primary)', borderRadius: 'var(--radius-sm)',
              '&:hover': { bgcolor: 'var(--color-primary-dark)' },
              '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: .8 },
              '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
            }}
          >
            {editDocuments.saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
